export interface PermissionScopedItem {
  permission?: string;
  children?: readonly PermissionScopedItem[];
}

export interface RolePermissionMap {
  role: string;
  permissions: readonly string[];
}

export interface PermissionUserLike {
  roles?: readonly string[] | null;
}

function normalizeValue(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '_');
}

function normalizePermission(value: string): string {
  return value.trim().toLowerCase();
}

export function normalizeRoles(roles: readonly string[] | null | undefined): string[] {
  return (roles ?? []).map(normalizeValue);
}

export function resolvePermissionsForRoles(
  roles: readonly string[] | null | undefined,
  matrix: readonly RolePermissionMap[],
): string[] {
  const normalizedRoles = normalizeRoles(roles);
  const permissions = new Set<string>();

  matrix.forEach(({ role, permissions: rolePermissions }) => {
    if (!normalizedRoles.includes(normalizeValue(role))) {
      return;
    }

    rolePermissions.forEach((permission) =>
      permissions.add(normalizePermission(permission)),
    );
  });

  return [...permissions];
}

export function filterItemsByPermissions<T extends PermissionScopedItem>(
  items: readonly T[],
  permissions: readonly string[] | null | undefined,
): T[] {
  const normalizedPermissions = new Set(
    (permissions ?? []).map(normalizePermission),
  );

  const hasAccess = (permission?: string) =>
    !permission || normalizedPermissions.has(normalizePermission(permission));

  return items.reduce<T[]>((result, item) => {
    if (item.children) {
      const children = filterItemsByPermissions(item.children as readonly T[], permissions);

      if (children.length > 0) {
        result.push({ ...item, children });
      }

      return result;
    }

    if (hasAccess(item.permission)) {
      result.push(item);
    }

    return result;
  }, []);
}

export function hasPermission(
  permissions: readonly string[] | null | undefined,
  permission: string,
): boolean {
  const normalizedPermissions = new Set(
    (permissions ?? []).map(normalizePermission),
  );

  return normalizedPermissions.has(normalizePermission(permission));
}

export function resolveEffectivePermissions(
  user: PermissionUserLike | null | undefined,
  matrix: readonly RolePermissionMap[],
): string[] {
  return resolvePermissionsForRoles(user?.roles, matrix);
}
